import { apiFetch } from './apiClient.js';
import { config } from '../config.js';

export interface FlowSummary {
  flowId: string;
  channel: string;
  version: string;
  updatedAt?: string;
}

export interface Flow {
  id: string;
  channel: string;
  version: string;
  tenantId: string;
  steps: unknown[];
  startStepId: string;
  updatedAt?: string;
}

export async function listFlows(tenantId: string): Promise<FlowSummary[]> {
  const res = await apiFetch<{ items: FlowSummary[] }>(
    `/v1/flows?tenantId=${encodeURIComponent(tenantId)}`,
    { baseUrl: config.apiBaseUrlFlow },
  );
  return res.items;
}

export async function getFlow(channel: string, tenantId?: string): Promise<Flow> {
  // Si platform-ops llama, pasa tenantId via query — el backend lo respeta cuando
  // el JWT trae custom:roles=platform-ops; si no, usa custom:tenant_id del claim.
  const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
  return apiFetch<Flow>(`/v1/flows/${encodeURIComponent(channel)}${qs}`, {
    baseUrl: config.apiBaseUrlFlow,
  });
}

/**
 * Autoguardado del builder. Misma semántica que `publishFlow` en cuanto al
 * tenant: se usa el del JWT, no el de la ruta.
 */
export async function saveDraft(channel: string, draft: unknown): Promise<{ savedAt: string }> {
  return apiFetch<{ savedAt: string }>(`/v1/flows/${encodeURIComponent(channel)}/draft`, {
    method: 'PUT',
    body: draft as Record<string, unknown>,
    baseUrl: config.apiBaseUrlFlow,
  });
}

export async function getDraft(
  channel: string,
): Promise<{ draft: unknown; savedAt: string } | null> {
  try {
    return await apiFetch<{ draft: unknown; savedAt: string }>(
      `/v1/flows/${encodeURIComponent(channel)}/draft`,
      { baseUrl: config.apiBaseUrlFlow },
    );
  } catch (err) {
    if (/404|not.?found/i.test(String((err as Error)?.message ?? ''))) return null;
    throw err;
  }
}

export async function discardDraft(channel: string): Promise<void> {
  await apiFetch<void>(`/v1/flows/${encodeURIComponent(channel)}/draft`, {
    method: 'DELETE',
    baseUrl: config.apiBaseUrlFlow,
  });
}

export async function publishFlow(
  channel: string,
  flow: Omit<Flow, 'channel'>,
): Promise<Flow> {
  return apiFetch<Flow>(`/v1/flows/${encodeURIComponent(channel)}`, {
    method: 'PUT',
    body: flow,
    baseUrl: config.apiBaseUrlFlow,
  });
}

export async function createFlowScaffold(
  channel: string,
  tenantId: string,
  opts: { name?: string } = {},
): Promise<Flow> {
  const scaffold: Omit<Flow, 'channel'> = {
    id: `flow-${channel}-${Date.now()}`,
    tenantId,
    version: '1.0.0',
    startStepId: 'welcome',
    steps: [
      {
        id: 'welcome',
        type: 'text',
        text: opts.name
          ? `Hola, bienvenido a ${opts.name}. ¿En qué puedo ayudarte?`
          : 'Hola, bienvenido. ¿En qué puedo ayudarte?',
        next: null,
      },
    ],
  };
  return publishFlow(channel, scaffold);
}
